// Viewer event observability — four surfaces.
//
// Mirrors the backend `operation_id`-as-spine pattern for citations
// + search overlays:
//   1) `console.info("<event>", {...})` — readable in DevTools.
//   2) `window.__viewerDebug` — last-event snapshot, inspectable any time.
//   3) DB-backed `viewer_events` table via `POST /observability/viewer-event`.
//      Queryable from CLI / dashboards without DevTools.
//   4) Cloud Logging structlog event `viewer.event.persisted` emitted
//      backend-side on insert. Correlates with operation_id when present.
//
// All events share a `runId` (per-tab counter) so the open → autojump →
// highlight chain — and any interleaved search events — can be threaded
// together. The SPA-generated `sessionId` (stable per browser tab via
// sessionStorage) disambiguates `runId` across tabs.
//
// Phase 1 of the Viewer Anchoring Epic.

import { api } from '../../api';

// ── Tab-stable session ID ────────────────────────────────────────────
// Stored in sessionStorage so it persists across page refreshes within
// the same tab but clears on tab close. crypto.randomUUID() is widely
// supported in modern browsers; if unavailable, fall back to a non-cryptographic
// random string — telemetry doesn't need cryptographic-grade uniqueness.
const SESSION_ID = (() => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return _fallbackId();
  }
  const KEY = '__chargestack_viewer_session_id';
  let id = null;
  try {
    id = window.sessionStorage.getItem(KEY);
  } catch {
    /* private mode etc. */
  }
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : _fallbackId();
    try { window.sessionStorage.setItem(KEY, id); } catch { /* ignore */ }
  }
  return id;
})();

function _fallbackId() {
  return 'sess-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
}

// ── Per-tab run counter ──────────────────────────────────────────────
let _runId = 0;

// ── Active document — set by startRun, used by subsequent emitters ──
// The legacy logAutoJump / logHighlight call sites don't pass
// documentId in their bodies (it wasn't needed when this was console.info
// only). Now that we POST to a doc-scoped endpoint we need it; we stash
// it from startRun so we don't have to thread it through every caller.
let _activeDocumentId = null;

// ── Optional operation_id from the last chat response ────────────────
// Set by the chat client when it receives `X-Operation-Id`. Citation
// clicks shortly after a chat response inherit this for end-to-end
// correlation in the trace dump. Reset to null when the user starts a
// fresh chat (so a stale id doesn't bleed into unrelated clicks).
let _operationId = null;
export function setActiveOperationId(opId) { _operationId = opId || null; }
export function getActiveOperationId() { return _operationId; }

// ── DB-backed persistence (Surface 3) ────────────────────────────────
// Fire-and-forget. NEVER awaited by callers. Failure to POST must never
// block the citation render — telemetry is observational.
function postEvent(eventType, body) {
  if (!body) return;
  // Fall back to the active run's documentId so legacy emit sites
  // (logAutoJump / logHighlight from PdfPreview) don't have to be
  // re-plumbed. startRun sets _activeDocumentId on every citation
  // open, so any subsequent event in the same run inherits it.
  const documentId = body.documentId || _activeDocumentId;
  if (!documentId) return;
  try {
    api.post('/observability/viewer-event', {
      event_type: eventType,
      document_id: documentId,
      chunk_id: body.chunkId || null,
      session_id: SESSION_ID,
      run_id: typeof body.runId === 'number' ? body.runId : _runId,
      operation_id: body.operationId || _operationId || null,
      payload: body,
    }).catch(() => { /* swallow */ });
  } catch {
    /* swallow */
  }
}

// ── Window-attached state (Surface 2) ────────────────────────────────
function _setSnapshot(patch) {
  if (typeof window === 'undefined') return;
  window.__viewerDebug = { ...(window.__viewerDebug || {}), ...patch };
}

// ── Citation emitters (the existing surface, now four-way) ───────────

export function startRun(meta) {
  _runId += 1;
  // Stash documentId for subsequent events in the same run that don't
  // pass it explicitly (logAutoJump / logHighlight bodies historically
  // omit it — they predate the POST surface).
  _activeDocumentId = (meta && meta.documentId) || null;
  const state = {
    sessionId: SESSION_ID,
    runId: _runId,
    operationId: _operationId,
    startedAt: new Date().toISOString(),
    ...meta,
    autoJump: null,
    highlight: null,
  };
  _setSnapshot(state);
  // eslint-disable-next-line no-console
  console.info('citation.open', { runId: _runId, ...meta });
  postEvent('citation.open', { ...meta, runId: _runId });
  return state;
}

export function logAutoJump(info) {
  _setSnapshot({ autoJump: info });
  // eslint-disable-next-line no-console
  console.info('citation.autojump', { runId: _runId, ...info });
  postEvent('citation.autojump', { ...info, runId: _runId });
}

export function logHighlight(info) {
  _setSnapshot({ highlight: info });
  // eslint-disable-next-line no-console
  console.info('citation.highlight', { runId: _runId, ...info });
  postEvent('citation.highlight', { ...info, runId: _runId });
}

// ── Deep-link emitter (replaces inline console.info in DocumentDeepLink) ──

export function logDeeplink(info) {
  _setSnapshot({ deeplink: info });
  // eslint-disable-next-line no-console
  console.info('citation.deeplink', info);
  postEvent('citation.deeplink', { ...info, runId: _runId });
}

// ── Locate emitters — chunk_id → EvidenceLocation resolution ─────────
// Used by MediaViewer (and any future format that calls the chunk
// locate endpoint) to record whether the SOTA chunk-backed pinpoint
// path succeeded. Failure modes: synthetic_id (#read#N from
// read_document_text), 404 (chunk not in index), no_location (chunk
// row has location.kind=='none'), network. Pairs with the existing
// citation.deeplink + citation.highlight events so a CLI run of
// `fetch_viewer_events.py <docId>` shows the full open → locate →
// highlight chain.

export function logLocateAttempt(info) {
  _setSnapshot({ locate: { attempt: info } });
  // eslint-disable-next-line no-console
  console.info('citation.locate.attempt', { runId: _runId, ...info });
  postEvent('citation.locate.attempt', { ...info, runId: _runId });
}

export function logLocateSuccess(info) {
  _setSnapshot({ locate: { ...((window.__viewerDebug || {}).locate || {}), success: info } });
  // eslint-disable-next-line no-console
  console.info('citation.locate.success', { runId: _runId, ...info });
  postEvent('citation.locate.success', { ...info, runId: _runId });
}

export function logLocateFail(info) {
  _setSnapshot({ locate: { ...((window.__viewerDebug || {}).locate || {}), fail: info } });
  // eslint-disable-next-line no-console
  console.info('citation.locate.fail', { runId: _runId, ...info });
  postEvent('citation.locate.fail', { ...info, runId: _runId });
}

// ── Search emitters (new in Phase 1) ─────────────────────────────────

export function logSearchQuery(info) {
  _setSnapshot({ search: { ...((window.__viewerDebug || {}).search || {}), query: info } });
  // eslint-disable-next-line no-console
  console.info('search.query', { runId: _runId, ...info });
  postEvent('search.query', { ...info, runId: _runId });
}

export function logSearchOverlay(info) {
  _setSnapshot({ search: { ...((window.__viewerDebug || {}).search || {}), lastOverlay: info } });
  // eslint-disable-next-line no-console
  console.info('search.overlay', { runId: _runId, ...info });
  postEvent('search.overlay', { ...info, runId: _runId });
}

// ── Media preview emitters ───────────────────────────────────────────
// Fire on <img>/<video> fetch start + failure so broken-preview
// triage doesn't require DevTools. Pairs with `media.fetch_*`
// vocabulary on viewer_events. Callers MUST pass `documentId`
// explicitly — preview components fire outside of citation runs so
// the _activeDocumentId fallback isn't safe here.
//
// payload contract (for ops dashboards + the server-side three-surface
// whitelist in observability_routes.py):
//   url_source: 'gcs_signed' | 'api_fallback' | 'media_url_failed' | ...
//   status:     HTTP code (failed events; absent for start)
//   error_class: browser-side exception name (failed events)
//   latency_ms: roundtrip if measured (optional)

export function logMediaFetchStarted(info) {
  if (!info || !info.documentId) return;
  // eslint-disable-next-line no-console
  console.info('media.fetch_started', info);
  postEvent('media.fetch_started', { ...info, runId: _runId });
}

export function logMediaFetchFailed(info) {
  if (!info || !info.documentId) return;
  // eslint-disable-next-line no-console
  console.warn('media.fetch_failed', info);
  postEvent('media.fetch_failed', { ...info, runId: _runId });
}

// ── Read-only accessors ──────────────────────────────────────────────

export function currentRunId() { return _runId; }
export function currentSessionId() { return SESSION_ID; }
